import { useState, useMemo, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { MessageSquare, TrendingUp, Package, Calendar, Check, ChevronsUpDown, Percent, Hash, Users, BarChart2, LineChart as LineChartIcon, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, LabelList, Line, LineChart } from 'recharts';

import { useCellConversationStatistics } from '@/hooks/use-conversations';
import { useAllTenants } from '@/hooks/use-tenants';
import { CELLS, PRIMARY_CELL, findCell, tenantLabel, tenantOptionValue, type Cell } from '@/lib/cells/cells';
import { cellNotLoadedHeading, homeCellExclusions } from '@/lib/cells/cell-statistics';
import { CellFailuresAlert, CellTenantsUnavailable } from '@/components/cells/cell-failures-alert';
import { EXCLUDED_TENANT_IDS } from '@/lib/constants/excluded-tenants';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Page, PageHeader, PageTitle } from '@/components/common/page';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

type DateRange = 'day' | 'week' | 'month' | 'quarter' | 'all' | 'custom';
type TimelinePeriod = 'day' | 'week' | 'month' | 'year';
type TimelineAggregation = 'calendar' | 'rolling';

interface AnalyticsSearch {
  range?: DateRange;
  from?: string;
  to?: string;
  tenant?: string;
  cell?: string;
  period?: TimelinePeriod;
  aggregation?: TimelineAggregation;
}

export const Route = createFileRoute('/agent/analytics')({
  component: AgentAnalyticsDashboard,
  validateSearch: (search: Record<string, unknown>): AnalyticsSearch => ({
    range: search.range as DateRange | undefined,
    from: search.from as string | undefined,
    to: search.to as string | undefined,
    tenant: search.tenant as string | undefined,
    cell: findCell(search.cell as string | undefined)?.id,
    period: search.period as TimelinePeriod | undefined,
    aggregation: search.aggregation as TimelineAggregation | undefined,
  }),
});

interface CustomDateRange {
  from: Date | undefined;
  to: Date | undefined;
}

function AgentAnalyticsDashboard() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // Initialize state from URL params
  const [dateRange, setDateRange] = useState<DateRange>(search.range || 'month');
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>({
    from: search.from ? new Date(search.from) : undefined,
    to: search.to ? new Date(search.to) : undefined,
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(search.tenant || 'all');
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);
  const [cellFilter, setCellFilter] = useState<string>(
    CELLS.length > 1 ? search.cell || 'all' : PRIMARY_CELL.id,
  );
  // Whether the cell was set by picking a tenant rather than by the user, so
  // it doesn't narrow the tenant picker and "All Tenants" widens it back.
  // Only a cell the user chose goes in the URL, so a reload keeps the choice.
  const [cellFromTenant, setCellFromTenant] = useState(false);
  const [showCategoryPercentages, setShowCategoryPercentages] = useState(false);
  const [showAppPercentages, setShowAppPercentages] = useState(false);
  const [showTenantPercentages, setShowTenantPercentages] = useState(false);
  const [timelineChartType, setTimelineChartType] = useState<'bar' | 'line'>('bar');
  const [timelinePeriod, setTimelinePeriod] = useState<TimelinePeriod>(search.period || 'day');
  const [timelineAggregation, setTimelineAggregation] = useState<TimelineAggregation>(search.aggregation || 'calendar');

  // Sync state to URL
  useEffect(() => {
    const newSearch: AnalyticsSearch = {};

    if (dateRange !== 'month') newSearch.range = dateRange;
    if (dateRange === 'custom') {
      if (customDateRange.from) newSearch.from = customDateRange.from.toISOString().split('T')[0];
      if (customDateRange.to) newSearch.to = customDateRange.to.toISOString().split('T')[0];
    }
    if (selectedTenantId !== 'all') newSearch.tenant = selectedTenantId;
    if (CELLS.length > 1 && cellFilter !== 'all' && !cellFromTenant) newSearch.cell = cellFilter;
    if (timelinePeriod !== 'day') newSearch.period = timelinePeriod;
    if (timelineAggregation !== 'calendar') newSearch.aggregation = timelineAggregation;

    void navigate({ search: newSearch, replace: true });
  }, [dateRange, customDateRange, selectedTenantId, cellFilter, cellFromTenant, timelinePeriod, timelineAggregation, navigate]);

  // Auto-open date picker when custom range is selected
  useEffect(() => {
    if (dateRange === 'custom' && !customDateRange.from && !customDateRange.to) {
      setDatePickerOpen(true);
    }
  }, [dateRange, customDateRange.from, customDateRange.to]);
  const { data: tenants, isLoading: tenantsLoading, failures: tenantFailures } = useAllTenants();
  const sortedTenants = useMemo(() => {
    return tenants
      ?.filter(t => cellFilter === 'all' || cellFromTenant || t.deployment_cell === cellFilter)
      .sort((a, b) => a.company.localeCompare(b.company)) || [];
  }, [tenants, cellFilter, cellFromTenant]);

  const selectedTenant = tenants?.find(t => t.id === selectedTenantId);

  // A selected tenant is read from its own cell.
  useEffect(() => {
    if (selectedTenant?.deployment_cell && selectedTenant.deployment_cell !== cellFilter) {
      setCellFilter(selectedTenant.deployment_cell);
      setCellFromTenant(true);
    }
  }, [selectedTenant, cellFilter]);

  const showCells = CELLS.length > 1;
  // A selected tenant whose cell can't be told because a cell's tenant list
  // failed. Its numbers aren't shown rather than read from a guessed cell.
  const tenantCellUnknown =
    showCells &&
    selectedTenantId !== 'all' &&
    !selectedTenant &&
    !tenantsLoading &&
    tenantFailures.length > 0;
  // Each cell's query leaves out other cells' tenants. With a cell's tenant
  // list missing, the other cells' numbers may include copies of its tenants.
  const exclusionsIncomplete =
    showCells && selectedTenantId === 'all' && !tenantsLoading && tenantFailures.length > 0;

  const queriedCells = useMemo((): Cell[] => {
    if (!showCells) return [PRIMARY_CELL];
    // The tenant lists decide a tenant's cell and each cell's exclusions.
    if (tenantsLoading) return [];
    if (selectedTenantId !== 'all') {
      const tenantCell = findCell(selectedTenant?.deployment_cell);
      if (tenantCell) return [tenantCell];
      if (tenantCellUnknown) return [];
      // Every cell answered and none has the tenant.
      return [PRIMARY_CELL];
    }
    return cellFilter === 'all' ? CELLS : [findCell(cellFilter) ?? PRIMARY_CELL];
  }, [showCells, selectedTenantId, selectedTenant, cellFilter, tenantsLoading, tenantCellUnknown]);

  const handleCellChange = (value: string) => {
    setCellFilter(value);
    setCellFromTenant(false);
    if (selectedTenant?.deployment_cell !== value) {
      setSelectedTenantId('all');
    }
  };

  // Calculate date range (memoized to prevent infinite loop)
  // Always round to complete calendar periods (weeks/months) based on grouping
  const { date_from, date_to } = useMemo(() => {
    if (dateRange === 'all') {
      return { date_from: undefined, date_to: undefined };
    }

    // Handle custom date range (supports partial ranges: only from, only to, or both)
    if (dateRange === 'custom') {
      if (!customDateRange.from && !customDateRange.to) {
        return { date_from: undefined, date_to: undefined };
      }

      let date_from: string | undefined;
      let date_to: string | undefined;

      if (customDateRange.from) {
        const fromDate = new Date(Date.UTC(
          customDateRange.from.getFullYear(),
          customDateRange.from.getMonth(),
          customDateRange.from.getDate()
        ));
        date_from = fromDate.toISOString();
      }

      if (customDateRange.to) {
        const toDate = new Date(Date.UTC(
          customDateRange.to.getFullYear(),
          customDateRange.to.getMonth(),
          customDateRange.to.getDate() + 1 // End of day
        ));
        date_to = toDate.toISOString();
      }

      return { date_from, date_to };
    }

    const now = new Date();
    let fromDate: Date;
    let toDate: Date;

    // Calculate raw date range
    switch (dateRange) {
      case 'day':
        fromDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
        toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
        break;
      case 'week':
        fromDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 7));
        toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
        break;
      case 'month':
        fromDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 30));
        toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
        break;
      case 'quarter':
        fromDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 90));
        toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
        break;
      default:
        fromDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 30));
        toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
    }

    // Round based on timeline period (only for calendar-based aggregation)
    if (timelineAggregation === 'calendar') {
      if (timelinePeriod === 'week') {
        // Round to complete calendar weeks (Monday to Sunday)
        const fromDayOfWeek = fromDate.getUTCDay();
        const daysToMonday = fromDayOfWeek === 0 ? 6 : fromDayOfWeek - 1;
        fromDate.setUTCDate(fromDate.getUTCDate() - daysToMonday);

        const toDayOfWeek = toDate.getUTCDay();
        const daysToNextMonday = toDayOfWeek === 1 ? 0 : (8 - toDayOfWeek) % 7;
        toDate.setUTCDate(toDate.getUTCDate() + daysToNextMonday);
      } else if (timelinePeriod === 'month') {
        // Round to complete calendar months
        fromDate.setUTCDate(1);
        toDate.setUTCMonth(toDate.getUTCMonth() + 1);
        toDate.setUTCDate(1);
      } else if (timelinePeriod === 'year') {
        // Round to complete calendar years
        fromDate.setUTCMonth(0);
        fromDate.setUTCDate(1);
        toDate.setUTCFullYear(toDate.getUTCFullYear() + 1);
        toDate.setUTCMonth(0);
        toDate.setUTCDate(1);
      }
    }
    // For rolling aggregation or daily period - no rounding, use exact dates

    return {
      date_from: fromDate.toISOString(),
      date_to: toDate.toISOString()
    };
  }, [dateRange, timelinePeriod, timelineAggregation, customDateRange]);
  const {
    stats,
    perCell,
    tenantCellIds,
    failures: statsFailures,
    isLoading: statsLoading,
  } = useCellConversationStatistics(queriedCells, {
    tenant_id: selectedTenantId !== 'all' ? selectedTenantId : undefined,
    date_from,
    date_to,
    excludeTenantIds: (cell) =>
      showCells && selectedTenantId === 'all'
        ? homeCellExclusions(cell.id, tenants ?? [], EXCLUDED_TENANT_IDS)
        // A tenant picked on purpose is shown even when it's an internal one.
        : EXCLUDED_TENANT_IDS.filter((id) => id !== selectedTenantId),
  });
  const isLoading = statsLoading || (queriedCells.length === 0 && !tenantCellUnknown);

  const categoryEntries = Object.entries(stats?.category_distribution || {}).filter(([name]) => name !== 'Empty');
  const appEntries = Object.entries(stats?.app_distribution || {});
  const tenantEntries = Object.entries(stats?.tenant_distribution || {});
  const dailyEntries = Object.entries(stats?.daily_distribution || {});

  // Calculate totals for percentage (excluding Empty category)
  const totalCategoryCount = categoryEntries.reduce((sum, [, count]) => sum + count, 0);
  const totalAppCount = appEntries.reduce((sum, [, count]) => sum + count, 0);

  // Prepare chart data
  const categoryChartData = categoryEntries
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, count]) => ({
      name,
      count,
      percentage: totalCategoryCount > 0 ? (count / totalCategoryCount) * 100 : 0,
    }));

  const appChartData = appEntries
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, count]) => ({
      name,
      count,
      percentage: totalAppCount > 0 ? (count / totalAppCount) * 100 : 0,
    }));

  // Prepare tenant chart data
  const totalTenantCount = tenantEntries.reduce((sum, [, count]) => sum + count, 0);
  const tenantChartData = tenantEntries
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15) // Show top 15 tenants
    .map(([tenantId, count]) => {
      const tenant = tenants?.find(t => t.id === tenantId);
      const cell = showCells ? findCell(tenantCellIds.get(tenantId)) : undefined;
      const company = tenant?.company || tenantId.slice(0, 8);
      return {
        name: cell ? `${company} (${cell.residency})` : company,
        fullName: tenant?.company || tenantId,
        company,
        residency: cell?.residency,
        count,
        percentage: totalTenantCount > 0 ? (count / totalTenantCount) * 100 : 0,
      };
    });

  // Prepare daily timeline data with grouping
  const dailyChartData = useMemo(() => {
    // Daily - no grouping needed
    if (timelinePeriod === 'day') {
      return dailyEntries
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, count]) => ({
          date,
          count,
          formattedDate: new Date(date + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
        }));
    }

    const grouped = new Map<string, { count: number; startDate: Date; endDate: Date }>();
    const sortedEntries = dailyEntries.sort((a, b) => a[0].localeCompare(b[0]));

    if (sortedEntries.length === 0) return [];

    // Rolling window groupings (calculated backwards from end date)
    if (timelineAggregation === 'rolling') {
      const windowDays = timelinePeriod === 'week' ? 7 : timelinePeriod === 'month' ? 30 : 365;
      const lastDate = new Date(sortedEntries[sortedEntries.length - 1][0] + 'T00:00:00Z');
      const firstDate = new Date(sortedEntries[0][0] + 'T00:00:00Z');

      for (const [date, count] of sortedEntries) {
        const d = new Date(date + 'T00:00:00Z');
        // Calculate days from the end, going backwards
        const daysFromEnd = Math.floor((lastDate.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
        const windowIndex = Math.floor(daysFromEnd / windowDays);

        // Window end is calculated backwards from lastDate
        const windowEnd = new Date(lastDate);
        windowEnd.setUTCDate(windowEnd.getUTCDate() - windowIndex * windowDays);
        const windowStart = new Date(windowEnd);
        windowStart.setUTCDate(windowStart.getUTCDate() - windowDays + 1);

        // Clamp windowStart to firstDate if it goes before the data range
        if (windowStart < firstDate) {
          windowStart.setTime(firstDate.getTime());
        }

        const key = windowStart.toISOString().split('T')[0];
        const existing = grouped.get(key);
        if (existing) {
          existing.count += count;
          // Update endDate to track the actual range
          if (d > existing.endDate) existing.endDate = d;
        } else {
          grouped.set(key, { count, startDate: new Date(windowStart), endDate: d });
        }
      }

      return Array.from(grouped.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([, { count, startDate, endDate }]) => ({
          date: startDate.toISOString().split('T')[0],
          count,
          formattedDate: `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`,
        }));
    }

    // Calendar-based groupings
    for (const [date, count] of sortedEntries) {
      const d = new Date(date + 'T00:00:00Z');
      let key: string;

      if (timelinePeriod === 'week') {
        const dayOfWeek = d.getUTCDay();
        const monday = new Date(d);
        monday.setUTCDate(d.getUTCDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
        key = monday.toISOString().split('T')[0];
      } else if (timelinePeriod === 'month') {
        key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      } else {
        // year
        key = `${d.getUTCFullYear()}`;
      }

      const existing = grouped.get(key);
      if (existing) {
        existing.count += count;
      } else {
        grouped.set(key, { count, startDate: d, endDate: d });
      }
    }

    return Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, { count }]) => {
        let formattedDate: string;
        if (timelinePeriod === 'week') {
          formattedDate = `Week of ${new Date(date + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
        } else if (timelinePeriod === 'month') {
          formattedDate = new Date(date + '-01T00:00:00Z').toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
        } else {
          // year
          formattedDate = date;
        }
        return { date, count, formattedDate };
      });
  }, [dailyEntries, timelinePeriod, timelineAggregation]);

  const categoryChartConfig = {
    count: {
      label: "Conversations",
      color: "hsl(270, 70%, 60%)",
    },
    percentage: {
      label: "Percentage",
      color: "hsl(270, 70%, 60%)",
    },
  } satisfies ChartConfig;

  const appChartConfig = {
    count: {
      label: "Conversations",
      color: "hsl(238, 71%, 60%)",
    },
    percentage: {
      label: "Percentage",
      color: "hsl(238, 71%, 60%)",
    },
  } satisfies ChartConfig;

  const tenantChartConfig = {
    count: {
      label: "Conversations",
      color: "hsl(142, 76%, 36%)",
    },
    percentage: {
      label: "Percentage",
      color: "hsl(142, 76%, 36%)",
    },
  } satisfies ChartConfig;

  const dailyChartConfig = {
    count: {
      label: "Conversations",
      color: "hsl(217, 91%, 60%)",
    },
  } satisfies ChartConfig;

  // Format functions for each chart
  const formatCategoryValue = (value: number) => {
    if (showCategoryPercentages) {
      return `${value.toFixed(1)}%`;
    }
    return value.toString();
  };

  const formatAppValue = (value: number) => {
    if (showAppPercentages) {
      return `${value.toFixed(1)}%`;
    }
    return value.toString();
  };

  const formatTenantValue = (value: number) => {
    if (showTenantPercentages) {
      return `${value.toFixed(1)}%`;
    }
    return value.toString();
  };

  return (
    <Page>
      <PageHeader>
        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-row items-center justify-between">
            <div>
              <PageTitle>Agent Analytics</PageTitle>
              <p className="text-muted-foreground mt-1">
                Analytics and insights for your agent conversation data
              </p>
            </div>
            <Badge variant="secondary" className="text-sm">
              Live
            </Badge>
          </div>
          <div className="flex flex-row gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {dateRange === 'custom' ? (
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[280px] justify-start text-left font-normal",
                        !customDateRange.from && !customDateRange.to && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateRange.from && customDateRange.to ? (
                        `${format(customDateRange.from, "MMM d, yyyy")} - ${format(customDateRange.to, "MMM d, yyyy")}`
                      ) : customDateRange.from ? (
                        `From ${format(customDateRange.from, "MMM d, yyyy")}`
                      ) : customDateRange.to ? (
                        `Until ${format(customDateRange.to, "MMM d, yyyy")}`
                      ) : (
                        <span>Pick a date range</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex">
                      <div className="flex flex-col gap-1 border-r p-3 min-w-[140px]">
                        <p className="text-xs text-muted-foreground font-medium mb-1 px-2">Presets</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start"
                          onClick={() => { setDateRange('day'); setDatePickerOpen(false); }}
                        >
                          Last 24 hours
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start"
                          onClick={() => { setDateRange('week'); setDatePickerOpen(false); }}
                        >
                          Last 7 days
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start"
                          onClick={() => { setDateRange('month'); setDatePickerOpen(false); }}
                        >
                          Last 30 days
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start"
                          onClick={() => { setDateRange('quarter'); setDatePickerOpen(false); }}
                        >
                          Last 90 days
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start"
                          onClick={() => { setDateRange('all'); setDatePickerOpen(false); }}
                        >
                          All time
                        </Button>
                      </div>
                      <div className="flex flex-col">
                        <CalendarComponent
                          mode="range"
                          selected={{ from: customDateRange.from, to: customDateRange.to }}
                          onSelect={(range) => {
                            setCustomDateRange({ from: range?.from, to: range?.to });
                          }}
                          numberOfMonths={2}
                        />
                        <div className="flex items-center justify-between border-t p-3">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setCustomDateRange({ from: undefined, to: undefined }); }}
                            >
                              Clear
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!customDateRange.from && !customDateRange.to}
                              onClick={() => {
                                const date = customDateRange.from || customDateRange.to;
                                setCustomDateRange({ from: date, to: undefined });
                              }}
                            >
                              From only
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!customDateRange.from && !customDateRange.to}
                              onClick={() => {
                                const date = customDateRange.to || customDateRange.from;
                                setCustomDateRange({ from: undefined, to: date });
                              }}
                            >
                              Until only
                            </Button>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => { setDatePickerOpen(false); }}
                            disabled={!customDateRange.from && !customDateRange.to}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <Select value={dateRange} onValueChange={(value) => { setDateRange(value as DateRange); }}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select time range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Last 24 hours</SelectItem>
                    <SelectItem value="week">Last 7 days</SelectItem>
                    <SelectItem value="month">Last 30 days</SelectItem>
                    <SelectItem value="quarter">Last 90 days</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="custom">Custom range...</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {showCells && (
              <Select value={cellFilter} onValueChange={handleCellChange}>
                <SelectTrigger className="w-[150px]" aria-label="Cell">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Cell: All cells</SelectItem>
                  {CELLS.map(cell => (
                    <SelectItem key={cell.id} value={cell.id}>
                      Cell: {cell.residency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Popover open={tenantPopoverOpen} onOpenChange={setTenantPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={tenantPopoverOpen}
                  className="w-[200px] justify-between"
                >
                  {selectedTenantId === 'all'
                    ? 'All Tenants'
                    : selectedTenant ? tenantLabel(selectedTenant) : 'Select tenant'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <CellTenantsUnavailable failures={tenantFailures} signInInNewTab />
                <Command>
                  <CommandInput placeholder="Search tenants..." />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>{tenantsLoading ? 'Loading tenants...' : 'No tenant found.'}</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setSelectedTenantId('all');
                          if (cellFromTenant) {
                            setCellFilter('all');
                            setCellFromTenant(false);
                          }
                          setTenantPopoverOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedTenantId === 'all' ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        All Tenants
                      </CommandItem>
                      {tenantsLoading && (
                        <CommandItem value="loading tenants" disabled>
                          Loading tenants...
                        </CommandItem>
                      )}
                      {sortedTenants.map(tenant => (
                        <CommandItem
                          key={tenant.id}
                          value={tenantOptionValue(tenant)}
                          onSelect={() => {
                            setSelectedTenantId(tenant.id);
                            if (tenant.deployment_cell && tenant.deployment_cell !== cellFilter) {
                              setCellFilter(tenant.deployment_cell);
                              setCellFromTenant(true);
                            }
                            setTenantPopoverOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedTenantId === tenant.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          {tenantLabel(tenant)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </PageHeader>

      <div className="flex-1 flex flex-col gap-6 overflow-y-auto p-6">
        {exclusionsIncomplete && (
          <CellFailuresAlert
            failures={tenantFailures}
            heading={(cell) =>
              `${cell.residency} tenants didn't load - other cells' numbers may include copies of ${cell.residency} tenants' conversations`
            }
            signInInNewTab
          />
        )}
        {tenantCellUnknown && (
          <CellFailuresAlert
            failures={tenantFailures}
            heading={(cell) => `Can't tell which cell this tenant is in: ${cell.residency} tenants didn't load`}
            signInInNewTab
          />
        )}
        <CellFailuresAlert
          failures={statsLoading ? [] : statsFailures}
          heading={(cell) => cellNotLoadedHeading(cell, perCell.map(({ cell: loaded }) => loaded))}
          signInInNewTab
        />

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
              <MessageSquare className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-8 w-20 bg-muted animate-pulse rounded" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{tenantCellUnknown ? '—' : stats?.total_conversations ?? 0}</div>
                  {showCells ? (
                    <p className="text-muted-foreground text-xs" data-testid="cell-split">
                      {[
                        ...perCell.map(({ cell, total }) => `${cell.residency} ${total}`),
                        ...statsFailures.map(({ cell }) => `${cell.residency} did not load`),
                      ].join(' | ')}
                    </p>
                  ) : (
                    <p className="text-muted-foreground text-xs">Processed conversations</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Categories</CardTitle>
              <TrendingUp className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-8 w-20 bg-muted animate-pulse rounded" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{categoryEntries.length}</div>
                  <p className="text-muted-foreground text-xs">Unique categories</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Apps</CardTitle>
              <Package className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-8 w-20 bg-muted animate-pulse rounded" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{appEntries.length}</div>
                  <p className="text-muted-foreground text-xs">Connected apps</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Timeline Chart */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <CardTitle>Conversations Timeline</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Select value={timelinePeriod} onValueChange={(value) => { setTimelinePeriod(value as TimelinePeriod); }}>
                  <SelectTrigger className="w-[100px] h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Daily</SelectItem>
                    <SelectItem value="week">Weekly</SelectItem>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="year">Yearly</SelectItem>
                  </SelectContent>
                </Select>
                {timelinePeriod !== 'day' && (
                  <Select value={timelineAggregation} onValueChange={(value) => { setTimelineAggregation(value as TimelineAggregation); }}>
                    <SelectTrigger className="w-[100px] h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="calendar">Calendar</SelectItem>
                      <SelectItem value="rolling">Rolling</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setTimelineChartType(timelineChartType === 'bar' ? 'line' : 'bar'); }}
                      className="h-6 w-6 p-0"
                    >
                      {timelineChartType === 'bar' ? (
                        <LineChartIcon className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <BarChart2 className="h-3 w-3 text-muted-foreground" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {timelineChartType === 'bar' ? 'Switch to line chart' : 'Switch to bar chart'}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-[250px] w-full flex items-center justify-center">
                <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
              </div>
            ) : dailyChartData.length === 0 ? (
              <div className="h-[250px] w-full flex items-center justify-center text-muted-foreground">
                No daily data available
              </div>
            ) : (
              <ChartContainer config={dailyChartConfig} className="h-[250px] w-full">
                {timelineChartType === 'bar' ? (
                  <BarChart accessibilityLayer data={dailyChartData} margin={{ left: -20, right: 10, top: 20, bottom: 10 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="formattedDate"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      fontSize={12}
                      className="text-xs"
                    />
                    <YAxis tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" fill="var(--color-count)" radius={4}>
                      <LabelList dataKey="count" position="top" className="fill-foreground text-xs" />
                    </Bar>
                  </BarChart>
                ) : (
                  <LineChart accessibilityLayer data={dailyChartData} margin={{ left: -20, right: 10, top: 20, bottom: 10 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="formattedDate"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      fontSize={12}
                      className="text-xs"
                    />
                    <YAxis tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="var(--color-count)"
                      strokeWidth={2}
                      dot={{ fill: "var(--color-count)", r: 4 }}
                    />
                  </LineChart>
                )}
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Distribution Charts */}
        <div className="grid gap-6 grid-cols-1">
          {/* Category Distribution */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Category Distribution</CardTitle>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowCategoryPercentages(!showCategoryPercentages); }}
                      className="h-6 w-6 p-0"
                    >
                      {showCategoryPercentages ? (
                        <Hash className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <Percent className="h-3 w-3 text-muted-foreground" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {showCategoryPercentages ? 'Show numbers' : 'Show percentages'}
                  </TooltipContent>
                </Tooltip>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-[200px] w-full flex items-center justify-center">
                  <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
                </div>
              ) : (
                <ChartContainer config={categoryChartConfig} className="h-[200px] w-full">
                  <BarChart accessibilityLayer data={categoryChartData} margin={{ left: -20, right: 10, top: 20, bottom: 10 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="name"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      interval={0}
                      fontSize={12}
                      angle={0}
                      className="text-xs"
                      tick={{ width: 100 }}
                      tickFormatter={(value: string | number) => {
                        // Truncate long labels but allow wrapping
                        const strValue = String(value);
                        if (strValue.length > 20) return strValue.slice(0, 20) + '...';
                        return strValue;
                      }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey={showCategoryPercentages ? 'percentage' : 'count'}
                      fill={showCategoryPercentages ? "var(--color-percentage)" : "var(--color-count)"}
                      radius={4}
                    >
                      <LabelList
                        dataKey={showCategoryPercentages ? 'percentage' : 'count'}
                        position="top"
                        className="fill-foreground text-xs"
                        formatter={formatCategoryValue}
                      />
                    </Bar>
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>

          {/* Tenant Distribution - Only show when viewing all tenants */}
          {selectedTenantId === 'all' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <CardTitle>Conversations per Tenant</CardTitle>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setShowTenantPercentages(!showTenantPercentages); }}
                        className="h-6 w-6 p-0"
                      >
                        {showTenantPercentages ? (
                          <Hash className="h-3 w-3 text-muted-foreground" />
                        ) : (
                          <Percent className="h-3 w-3 text-muted-foreground" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {showTenantPercentages ? 'Show numbers' : 'Show percentages'}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="h-[200px] w-full flex items-center justify-center">
                    <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
                  </div>
                ) : tenantChartData.length === 0 ? (
                  <div className="h-[200px] w-full flex items-center justify-center text-muted-foreground">
                    No tenant data available
                  </div>
                ) : (
                  <ChartContainer config={tenantChartConfig} className="h-[200px] w-full">
                    <BarChart
                      accessibilityLayer
                      data={tenantChartData}
                      margin={{ left: -20, right: 10, top: 20, bottom: 10 }}
                    >
                      <CartesianGrid vertical={false} />
                      <XAxis
                        dataKey="name"
                        tickLine={false}
                        tickMargin={10}
                        axisLine={false}
                        interval={0}
                        fontSize={12}
                        angle={0}
                        className="text-xs"
                        tick={{ width: 100 }}
                        // Room for a wrapped name plus its cell on a third line.
                        height={showCells ? 50 : undefined}
                        tickFormatter={(value: string | number) => {
                          // Truncate the name only, so the cell stays visible.
                          const row = tenantChartData.find(r => r.name === value);
                          const company = row?.company ?? String(value);
                          const shown = company.length > 20 ? company.slice(0, 20) + '...' : company;
                          return row?.residency ? `${shown} (${row.residency})` : shown;
                        }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                      />
                      <ChartTooltip
                        content={<ChartTooltipContent />}
                        cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                      />
                      <Bar
                        dataKey={showTenantPercentages ? 'percentage' : 'count'}
                        fill={showTenantPercentages ? "var(--color-percentage)" : "var(--color-count)"}
                        radius={4}
                      >
                        <LabelList
                          dataKey={showTenantPercentages ? 'percentage' : 'count'}
                          position="top"
                          className="fill-foreground text-xs"
                          formatter={formatTenantValue}
                        />
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                )}
              </CardContent>
            </Card>
          )}

          {/* App Distribution */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Top Apps</CardTitle>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowAppPercentages(!showAppPercentages); }}
                      className="h-6 w-6 p-0"
                    >
                      {showAppPercentages ? (
                        <Hash className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <Percent className="h-3 w-3 text-muted-foreground" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {showAppPercentages ? 'Show numbers' : 'Show percentages'}
                  </TooltipContent>
                </Tooltip>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-[200px] w-full flex items-center justify-center">
                  <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
                </div>
              ) : (
                <ChartContainer config={appChartConfig} className="h-[200px] w-full">
                  <BarChart accessibilityLayer data={appChartData} margin={{ left: -20, right: 10, top: 20, bottom: 10 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="name"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      interval={0}
                      fontSize={12}
                      angle={0}
                      className="text-xs"
                      tick={{ width: 100 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey={showAppPercentages ? 'percentage' : 'count'}
                      fill={showAppPercentages ? "var(--color-percentage)" : "var(--color-count)"}
                      radius={4}
                    >
                      <LabelList
                        dataKey={showAppPercentages ? 'percentage' : 'count'}
                        position="top"
                        className="fill-foreground text-xs"
                        formatter={formatAppValue}
                      />
                    </Bar>
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Page>
  );
}
